/**
 * Current User Service
 * Stores and retrieves the current authenticated user data from /ris/api/auth/me endpoint
 */

export interface CurrentUserData {
  id: number;
  username: string;
  email: string;
  name: string;
  tenantID: string;
  roles: string[];
  facility_id: string;
  modality: string[];
  dbUser: {
    id: number;
    username: string;
    email: string;
    full_name: string;
    role: string;
    status: string;
    facility_id: string;
    reporting_modality_access: string[];
    department: unknown[];
  };
  tokenPayload: {
    exp: number;
    iat: number;
    jti: string;
    iss: string;
    aud: string;
    sub: string;
    typ: string;
    azp: string;
    sid: string;
    acr: string;
    'allowed-origins': string[];
    realm_access: {
      roles: string[];
    };
    resource_access: {
      account: {
        roles: string[];
      };
    };
    scope: string;
    email_verified: boolean;
    name: string;
    tenantID: string;
    preferred_username: string;
    given_name: string;
    family_name: string;
    email: string;
  };
}

export interface CurrentUserFromDatabase {
  id: number;
  full_name: string;
  gender: string;
  date_of_birth: string;
  username: string;
  email: string;
  mobile_number: string;
  role: string;
  sub_role: string | null;
  facility_id: string;
  status: string;
  profile_picture: string | null;
  employee_id: string | null;
  department: unknown[];
  qualification: string;
  experience_years: number;
  reporting_supervisor: string | null;
  shift_timing: string | null;
  assigned_counter: string | null;
  doctor_id: string;
  registration_number: string;
  specialty: string[] | string;
  signature: string;
  peer_reviewer: boolean;
  reporting_modality_access: string[];
  availability_hours: unknown[];
  availability_days: unknown[];
  shift_start_at: string | null;
  shift_end_at: string | null;
  /** When true, referring physicians may view approved reports in the viewer */
  report_access?: boolean | null;
  is_deleted: boolean;
  is_superadmin: boolean;
  deleted_at: string | null;
  created_by: number;
  modified_by: number | null;
  deleted_by: number | null;
  keycloak_userid: string;
  createdAt: string;
  updatedAt: string;
}

export interface CurrentUserResponse {
  success: boolean;
  message: string;
  data: CurrentUserData;
  fromDatabase: CurrentUserFromDatabase;
}

class CurrentUserService {
  private currentUser: CurrentUserResponse | null = null;

  /**
   * Store current user data from API response
   * @param userResponse - The response from /ris/api/auth/me endpoint
   */
  setCurrentUser(userResponse: CurrentUserResponse): void {
    this.currentUser = userResponse;
  }

  /**
   * Get the complete current user response
   * @returns CurrentUserResponse | null
   */
  getCurrentUser(): CurrentUserResponse | null {
    return this.currentUser;
  }

  /**
   * Get the user data object (from response.data)
   * @returns CurrentUserData | null
   */
  getUserData(): CurrentUserData | null {
    return this.currentUser?.data || null;
  }

  /**
   * Get the user data from database (from response.fromDatabase)
   * @returns CurrentUserFromDatabase | null
   */
  getUserFromDatabase(): CurrentUserFromDatabase | null {
    return this.currentUser?.fromDatabase || null;
  }

  /**
   * Get user ID
   * @returns number | null
   */
  getUserId(): number | null {
    return this.currentUser?.data?.id || null;
  }

  /**
   * Get user roles
   * @returns string[]
   */
  getUserRoles(): string[] {
    return this.currentUser?.data?.roles || [];
  }

  /**
   * Get user facility ID
   * @returns string | null
   */
  getFacilityId(): string | null {
    return this.currentUser?.data?.facility_id || null;
  }

  /**
   * Get user modalities
   * @returns string[]
   */
  getUserModalities(): string[] {
    return this.currentUser?.data?.modality || [];
  }

  /**
   * Get user sub_role from database
   * @returns string | null
   */
  getSubRole(): string | null {
    return this.currentUser?.fromDatabase?.sub_role || null;
  }

  /**
   * Check if user has a specific role
   * @param role - The role to check
   * @returns boolean
   */
  hasRole(role: string): boolean {
    return this.getUserRoles().includes(role);
  }

  /**
   * Check if user has access to a specific modality
   * @param modality - The modality to check
   * @returns boolean
   */
  hasModalityAccess(modality: string): boolean {
    return this.getUserModalities().includes(modality);
  }

  /**
   * Clear current user data
   */
  clearCurrentUser(): void {
    this.currentUser = null;
  }
}

// Export singleton instance
export const currentUserService = new CurrentUserService();
export default currentUserService;
